import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { bulkAssignmentNotifier } from '@/ws';
import { caseQueryKeys, useBulkAssignmentStatusQuery } from '@/queries/case';
import {
  bulkAssignmentRunReducer,
  initialBulkAssignmentRunState,
  selectCasesTotal,
  selectIsRunActive,
  selectProcessedCount,
} from './bulkAssignmentRun.reducer';
import { parseBulkAssignmentMessage } from './bulkAssignmentRun.snapshot';
import { BULK_ASSIGNMENT_HARD_TIMEOUT_MS, BULK_ASSIGNMENT_STATUS_POLL_MS } from '../constants';

const { logger } = Sentry;

/**
 * Single source of truth for the in-progress modal.
 * Two inputs feed one reducer: websocket messages (fast) and the status endpoint (authoritative).
 */
export const useBulkAssignmentRun = () => {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(bulkAssignmentRunReducer, initialBulkAssignmentRunState);

  const { correlationId, startedAt } = state;
  const isRunActive = selectIsRunActive(state);
  const wasRunActive = useRef(isRunActive);

  const { data: status, dataUpdatedAt } = useBulkAssignmentStatusQuery({
    refetchInterval: isRunActive ? BULK_ASSIGNMENT_STATUS_POLL_MS : false,
  });

  useEffect(
    () =>
      bulkAssignmentNotifier.on((data) =>
        dispatch({ type: 'ws/message', message: parseBulkAssignmentMessage(data), now: Date.now() }),
      ),
    [],
  );

  useEffect(() => {
    if (!dataUpdatedAt) return;
    dispatch({ type: 'rest/reconcile', status: status ?? null, fetchedAt: dataUpdatedAt });
  }, [status, dataUpdatedAt]);

  useEffect(() => {
    if (startedAt === null) return undefined;

    const timer = setTimeout(
      () => {
        // Getting here means the saga is stuck server-side, which blocks every other user too.
        logger.warn(logger.fmt`Bulk assignment run ${correlationId ?? 'unknown'} exceeded the hard cap`);
        dispatch({ type: 'run/finish' });
        toast.warning('This bulk update appears to be stuck. Contact support if you cannot start a new one.');
      },
      Math.max(0, startedAt + BULK_ASSIGNMENT_HARD_TIMEOUT_MS - Date.now()),
    );

    return () => clearTimeout(timer);
  }, [startedAt, correlationId]);

  useEffect(() => {
    const justFinished = wasRunActive.current && !isRunActive;
    wasRunActive.current = isRunActive;

    if (justFinished) {
      queryClient.invalidateQueries({ queryKey: caseQueryKeys.getBulkAssignmentCases() });
    }
  }, [isRunActive, queryClient]);

  const startRun = useCallback(
    (startedCorrelationId: string, expectedTotal: number) =>
      dispatch({ type: 'run/start', correlationId: startedCorrelationId, expectedTotal, now: Date.now() }),
    [],
  );

  const operations = useMemo(() => Object.values(state.operations), [state.operations]);

  return {
    startRun,
    isRunActive,
    processedCount: selectProcessedCount(state),
    casesTotal: selectCasesTotal(state),
    operations,
    relatedEmployees: state.relatedEmployees,
  };
};
